import { useEffect, type ReactNode } from 'react';
import { useSession } from '../auth/session';
import { useTraduction } from './langue';

/**
 * Mémorise la langue de l'interface sur le compte, pas seulement dans le navigateur.
 *
 * `useTraduction` persiste déjà le choix dans `localStorage` — suffisant pour un parent
 * qui revient sur le même appareil, mais pas pour les e-mails transactionnels : ils
 * partent d'un serveur qui ne connaît que ce que le compte porte. Dès qu'une session est
 * ouverte et que la langue affichée diverge de celle enregistrée, cet effet la pousse au
 * compte — silencieusement, en tâche de fond, sans bloquer ni annoncer quoi que ce soit :
 * un échec de synchronisation laisse simplement le choix valable pour la session en
 * cours, exactement comme avant.
 *
 * Volontairement à sens unique : au chargement, la langue affichée ne se réaligne jamais
 * sur celle du compte. Un parent qui vient de choisir « English » dans son navigateur ne
 * doit pas se voir basculé de force vers la langue d'un compte visité depuis un autre
 * appareil — ce serait un changement de langue que personne n'a demandé, sur cet écran.
 */
export function SynchronisationLangueCompte({ children }: { children: ReactNode }) {
  const { locale } = useTraduction();
  const { client, profil, rafraichirProfil } = useSession();

  useEffect(() => {
    if (!profil || profil.locale === locale) return;
    let annule = false;
    client
      .request('/auth/locale', { method: 'PATCH', body: { locale } })
      .then(() => {
        if (!annule) void rafraichirProfil();
      })
      .catch(() => {
        // Best-effort : le choix reste valable dans le navigateur même si la
        // synchronisation vers le compte échoue.
      });
    return () => {
      annule = true;
    };
  }, [client, locale, profil, rafraichirProfil]);

  return <>{children}</>;
}
